import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Web pública: los activos internos se muestran solo por número correlativo
// (sin nombres comerciales, sin detalles técnicos internos).
const SECURITY_LAYERS = [
  { code: '01', description: 'Capa interna de protección y revisión defensiva.' },
  { code: '02', description: 'Capa de monitoreo continuo y alerta operativa.' },
  { code: '03', description: 'Capa interna de control y detección temprana.' },
  { code: '04', description: 'Mecanismo de contención defensiva controlada.' },
  { code: '05', description: 'Capa de resguardo operativo interno.' },
  { code: '06', description: 'Registro, trazabilidad y apoyo forense interno.' },
  { code: '09', description: 'Reportes ejecutivos internos sanitizados.' },
  { code: '16', description: 'Protección contextual de información sensible.' }
];

const SecurityPage = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background-dark font-display">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />

        <main className="py-12">
          {/* Hero */}
          <div className="text-center mb-12 max-w-3xl mx-auto" data-testid="security-hero">
            <div className="inline-flex items-center gap-2 px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold mb-4">
              <span className="material-symbols-outlined text-base">shield</span>
              Seguridad
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-gray-900 dark:text-white mb-4">
              Sistema de Seguridad de GPT-JDP
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              GPT-JDP incorpora capas internas activas de protección, monitoreo, trazabilidad y contención
              defensiva orientadas a resguardar la operación de la plataforma y la información que circula por ella.
            </p>
          </div>

          {/* Aviso legal prudente */}
          <div className="max-w-4xl mx-auto mb-10 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <p className="text-sm text-yellow-800 dark:text-yellow-200">
              <strong>Aviso importante:</strong> Estas capas están orientadas a apoyar la operación interna
              y reducir la exposición a riesgos conocidos, pero no se declara ni garantiza seguridad absoluta,
              inviolabilidad, ni cumplimiento total con normas o marcos internacionales sin auditoría independiente.
            </p>
          </div>

          {/* Grid de capas activas — solo por número */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16" data-testid="security-layers-grid">
            {SECURITY_LAYERS.map((layer) => (
              <div
                key={layer.code}
                className="bg-white dark:bg-[#1a2530] rounded-xl p-6 shadow-sm border border-gray-100 dark:border-[#283039] hover:shadow-lg transition-shadow flex flex-col items-center text-center"
                data-testid={`security-layer-${layer.code}`}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary/10 mb-3">
                  <span className="text-2xl font-black text-primary">{layer.code}</span>
                </div>
                <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded text-xs font-bold mb-2">
                  ACTIVA
                </span>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                  {layer.description}
                </p>
              </div>
            ))}
          </div>

          {/* Compromiso operativo */}
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 rounded-2xl p-8 mb-10">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Compromiso operativo</h2>
            <ul className="space-y-3 text-gray-700 dark:text-gray-300">
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary mt-0.5">check_circle</span>
                <span>Monitoreo continuo y trazabilidad de eventos operativos.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary mt-0.5">check_circle</span>
                <span>Mitigación proactiva ante intentos de abuso detectados.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary mt-0.5">check_circle</span>
                <span>Protección contextual para reducir la exposición de información sensible.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary mt-0.5">check_circle</span>
                <span>Registro forense interno para apoyar auditorías y revisiones.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="material-symbols-outlined text-primary mt-0.5">check_circle</span>
                <span>Mecanismos de contención defensiva ante escenarios críticos.</span>
              </li>
            </ul>
          </div>

          {/* Alcance y limitaciones */}
          <div className="max-w-4xl mx-auto bg-white dark:bg-[#1a2530] rounded-2xl p-8 border border-gray-200 dark:border-[#283039]">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Alcance y limitaciones</h2>
            <div className="prose dark:prose-invert max-w-none">
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                Los mecanismos internos de seguridad de GPT-JDP están orientados a la operación de la plataforma.
                Por prudencia y transparencia, declaramos lo siguiente:
              </p>
              <ul className="mt-4 space-y-2 text-gray-600 dark:text-gray-300">
                <li>• Ningún sistema informático es 100% inviolable. Las capas internas <strong>reducen</strong> la exposición al riesgo, sin eliminarla por completo.</li>
                <li>• Los reportes de módulos informativos (Ciberseguridad, Legal, Contabilidad, Finanzas) <strong>no reemplazan la asesoría profesional</strong> certificada.</li>
                <li>• La plataforma no declara cumplimiento total con normas internacionales sin auditoría independiente formal.</li>
                <li>• La arquitectura interna profunda y otros componentes reservados <strong>no forman parte de esta descripción pública</strong> y permanecen protegidos como activos retenidos por <strong>Nexus Nova Systems SpA</strong>.</li>
              </ul>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
};

export default SecurityPage;
